import { useState, useEffect } from 'react'

const WHITEPAPER_PASSWORD = 'dizee2025'
const STORAGE_KEY = 'dizee-whitepaper-unlock'

/* Password gate: force light theme so input is not overridden by .dark .bg-white */
function PasswordGate({ onSubmit, password, setPassword, error }: {
  onSubmit: (e: React.FormEvent) => void
  password: string
  setPassword: (s: string) => void
  error: boolean
}) {
  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-8">
      <div className="w-full max-w-sm">
        <div className="mb-8">
          <p className="text-[#111] text-lg font-semibold mb-1">DIZEE Whitepaper</p>
          <p className="text-[#111]/70 text-sm">Enter the password to continue.</p>
        </div>
        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value)
              }}
              placeholder="Password"
              autoFocus
              autoComplete="off"
              className="w-full rounded border border-[#ccc] px-4 py-3 text-sm placeholder:text-[#111]/40 focus:outline-none focus:ring-2 focus:ring-[#111]/20 focus:border-[#111]/40"
              style={{ backgroundColor: '#ffffff', color: '#111111' }}
              aria-label="Whitepaper password"
            />
            {error && (
              <p className="mt-2 text-xs text-red-700">Incorrect password. Try again.</p>
            )}
          </div>
          <button
            type="submit"
            className="w-full rounded bg-[#111] px-4 py-3 text-sm font-medium text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[#111]/30"
          >
            Enter
          </button>
        </form>
      </div>
    </div>
  )
}

/* Whitepaper layout components (Momento-style) */
function Section({ id, title, children }: { id: string; title: string; children: React.ReactNode }) {
  return (
    <section id={id} className="mt-16 first:mt-0">
      <h2 className="mb-6 border-b border-[#111]/15 pb-3 text-2xl font-bold text-[#111]">
        {title}
      </h2>
      <div className="space-y-4 text-[#111]/90 leading-relaxed">{children}</div>
    </section>
  )
}

function Callout({ variant = 'primary', children }: { variant?: 'primary' | 'accent'; children: React.ReactNode }) {
  const bg = variant === 'primary' ? 'bg-[#111]/[0.06]' : 'bg-[#111]/[0.04]'
  const border = variant === 'primary' ? 'border-[#111]/15' : 'border-[#111]/10'
  return (
    <div className={`my-6 rounded-xl border ${border} ${bg} p-6`}>
      <div className="text-base font-medium text-[#111]">{children}</div>
    </div>
  )
}

function CodeBlock({ children }: { children: string }) {
  return (
    <pre className="my-6 overflow-x-auto rounded-xl border border-[#111]/15 bg-[#111]/[0.04] p-4">
      <code className="text-sm text-[#111] font-mono">{children}</code>
    </pre>
  )
}

function Diagram({ children }: { children: string }) {
  return (
    <pre className="my-6 overflow-x-auto rounded-xl border border-[#111]/15 bg-[#111]/[0.03] p-6 font-mono text-sm text-[#111]/80 leading-relaxed">
      {children}
    </pre>
  )
}

function DataFlowDiagram() {
  return (
    <div className="my-8 rounded-xl border border-[#111]/15 bg-[#111]/[0.02] p-8">
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-[#111]/60">Signal → Intelligence pipeline</p>
      <pre className="font-mono text-sm text-[#111]/90 leading-loose">
{`  ┌─────────────┐     ┌──────────────┐     ┌─────────────────┐
  │  Artists &   │     │  Aggregation  │     │  Prediction     │
  │  Labels     │ --> │  & Anonymized │ --> │  & Market       │
  │  (DIZEE)    │     │  Signals      │     │  Intelligence   │
  └─────────────┘     └──────────────┘     └─────────────────┘
        │                     │                      │
        ▼                     ▼                      ▼
  ticket velocity       regional demand        tour success
  sellout curves       media sentiment        revenue forecasts
  release timing       narrative shifts       market heat maps`}
      </pre>
    </div>
  )
}

function ComparisonTable() {
  return (
    <div className="my-8 overflow-hidden rounded-xl border border-[#111]/15">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-[#111]/15 bg-[#111]/[0.05]">
            <th className="px-6 py-3 text-sm font-semibold text-[#111]">Dimension</th>
            <th className="px-6 py-3 text-sm font-semibold text-[#111]">Traditional industry</th>
            <th className="px-6 py-3 text-sm font-semibold text-[#111]">DIZEE</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#111]/10">
          <tr>
            <td className="px-6 py-4 font-medium text-[#111]">Data source</td>
            <td className="px-6 py-4 text-sm text-[#111]/80">Siloed, self-reported, lagging</td>
            <td className="px-6 py-4 text-sm text-[#111]/80">Unified, real-time, behavioral</td>
          </tr>
          <tr>
            <td className="px-6 py-4 font-medium text-[#111]">Market prediction</td>
            <td className="px-6 py-4 text-sm text-[#111]/80">Gut feel, past tours only</td>
            <td className="px-6 py-4 text-sm text-[#111]/80">Signals + world events + history</td>
          </tr>
          <tr>
            <td className="px-6 py-4 font-medium text-[#111]">Defensibility</td>
            <td className="px-6 py-4 text-sm text-[#111]/80">Tool adoption only</td>
            <td className="px-6 py-4 text-sm text-[#111]/80">Data moat plus predictive intelligence</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

function PredictionModelDiagram() {
  return (
    <div className="my-8 rounded-xl border border-[#111]/15 bg-[#111]/[0.02] p-8">
      <p className="mb-4 text-xs font-semibold uppercase tracking-wider text-[#111]/60">Prediction inputs (simplified)</p>
      <pre className="font-mono text-sm text-[#111]/85 leading-relaxed">
{`  Outcome (e.g. sellout speed, revenue) =
    f(
      historical_velocity,
      regional_economic_index,
      media_sentiment_delta,
      narrative_events,
      genre_market_heat,
      calendar_crowding
    )`}
      </pre>
      <p className="mt-4 text-xs text-[#111]/60">No single input dominates; the combination is the moat.</p>
    </div>
  )
}

export default function WhitepaperPage() {
  const [unlocked, setUnlocked] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)

  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY)
    if (stored === 'true') setUnlocked(true)
  }, [])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === WHITEPAPER_PASSWORD) {
      sessionStorage.setItem(STORAGE_KEY, 'true')
      setUnlocked(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  if (!unlocked) {
    return (
      <PasswordGate
        onSubmit={handleSubmit}
        password={password}
        setPassword={(s) => { setPassword(s); setError(false) }}
        error={error}
      />
    )
  }

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#111]">
      {/* Header */}
      <div className="border-b border-[#111]/10 bg-gradient-to-b from-[#fafafa] to-[#f5f5f5]">
        <div className="mx-auto max-w-4xl px-4 py-16 text-left sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold tracking-tight text-[#111]">
            DIZEE: Data, Intelligence, and the Future of Music
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-[#111]/75">
            The data backbone of music and path to market-level prediction
          </p>
          <p className="mt-4 text-sm text-[#111]/60">
            Edward Contreras · {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} · {new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
          </p>
        </div>
      </div>

      <article className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8">
        <Section id="abstract" title="Abstract">
          <p>
            DIZEE is building the data backbone of music and entertainment. The platform’s power lies not only in tools
            for creators but in <strong>who uses it</strong> and <strong>what we learn</strong> from their behavior.
            This whitepaper outlines how proprietary logic, aggregated signals, and predictive intelligence form a
            defensible moat, and how that moat enables market-level prediction.
          </p>
          <Callout variant="primary">
            We are building the Palantir of the music industry: the place where artists and labels put their data, and
            where the world learns how entertainment drives economic and sociological outcomes.
          </Callout>
        </Section>

        <Section id="moat" title="1. The Moat: Logic as Code">
          <p>
            The intense logic that powers DIZEE’s insights is, in effect, proprietary code. That complexity is
            deliberate. Getting the right combination of rules, signals, and data pipelines is difficult, and that
            difficulty is a moat. Even if someone tried to copy the system at face value, reproducing the same quality
            of insights would take months of iteration.
          </p>
          <p>
            When we layer in future-facing intelligence, predictions informed by world events, historical data, and
            behavioral patterns, the platform becomes uniquely powerful. Accurate predictions built on that
            foundation are what position DIZEE to lead the industry.
          </p>
          <CodeBlock>{`Insight_quality = Logic(data, signals, world_events, history)
Replication_cost ≈ months of iteration + access to equivalent data`}</CodeBlock>
        </Section>

        <Section id="data-moat" title="2. The Data Moat: Who Uses the Platform">
          <p>
            The concept is driven by our ability to onboard artists, labels, and rights holders onto the platform and
            to collect their data. Once that data is flowing, the depth and breadth of the dataset define what we can
            know.
          </p>
          <p>
            Even if we remove the option to see which data pertains to which entity, full anonymization, we still produce
            insights that trump anyone else’s, simply because of <strong>who is using DIZEE</strong>. The more
            high-signal participants we have, the stronger the aggregate intelligence.
          </p>
          <Callout variant="accent">
            The edge is not tool adoption. It is our ability to onboard the right participants and turn their data
            into intelligence no one else can replicate.
          </Callout>
          <ComparisonTable />
        </Section>

        <Section id="high-signal" title="3. The Power of High-Signal Data">
          <p>
            A single major artist or label using DIZEE for tours and releases generates extraordinarily rich data:
            sellout speed, ticket tiers, geographic response, media sentiment before and after events, and how narrative
            shifts (controversy, apology, cultural moments) correlate with demand. That data allows us to predict
            outcomes for other artists under similar circumstances.
          </p>
          <p>
            It also reflects how a <strong>region</strong> responds economically and socially to entertainment: who
            pays, how fast, and why. Music is a subset of entertainment, and entertainment is a major factor in how
            society expresses itself economically and sociologically. DIZEE becomes an experiment in understanding why
            society reacts the way it does to changes in the world, with music and live events as the lens.
          </p>
          <DataFlowDiagram />
        </Section>

        <Section id="markets" title="4. From Signals to Market Prediction">
          <p>
            The same data that improves an artist’s own decisions can be aggregated to <strong>predict markets</strong>.
            Regional demand curves, genre heat, calendar crowding, and media narratives are not just artist-level
            signals. They are macroeconomic and sociological indicators. DIZEE can answer questions like:
          </p>
          <ul className="list-disc list-inside space-y-2 text-[#111]/90">
            <li>Which cities will over- or underperform for a given genre in the next 12–24 months?</li>
            <li>How do political or cultural events in a region affect willingness to pay for live entertainment?</li>
            <li>When does media sentiment lead demand vs. lag it, and by how much?</li>
            <li>What combination of timing, narrative, and pricing maximizes revenue for a given artist profile?</li>
          </ul>
          <p>
            These are not “creator tools.” They are <strong>market intelligence</strong>, valuable to promoters, venues,
            labels, and investors. The data can be anonymized; the insights remain unique because they are built on who
            is on the platform.
          </p>
          <PredictionModelDiagram />
          <Diagram>{`Market prediction (conceptual)
  |
  |     *  ← high confidence (enough data, stable signals)
  |   *   *
  | *   *   *
  |  *   *     *  ← lower confidence (sparse or volatile)
  +────────────────────────── Time / geography`}</Diagram>
        </Section>

        <Section id="intelligence-layer" title="5. The Intelligence Layer: Public Data, Private Data, and Cross-Analysis">
          <p>
            We are building AI that cross-analyzes <strong>world events</strong> and other public data with our
            <strong> private data</strong> (behavioral, tour, and release data from artists and labels on the platform).
            The model is trained to understand, at a high level, the public information available to everyone, news,
            economic indicators, sentiment, and cultural moments, and to combine that with what only DIZEE sees:
            who is selling out where, how fast, at what price, and under what narrative conditions.
          </p>
          <p>
            Public data alone cannot tell you how a specific market or segment will behave. Private data alone cannot
            tell you how the next election, policy change, or cultural shift will affect demand. The intelligence layer
            learns the relationship between the two. That combination yields higher-level insights (e.g. how a type of
            world event correlates with a type of audience response) and keeps our predictions ahead of anyone who has
            only one side of the equation. Competitors may have rich public data or their own siloed private data; they
            do not have both, continuously aligned and used to train a single model. That is the technical moat.
          </p>
          <Callout variant="accent">
            The goal is not to “understand everything,” but to train models that reliably link world context to
            entertainment outcomes. The more high-quality private data we have, the smarter and more defensible that
            link becomes.
          </Callout>
        </Section>

        <Section id="society" title="6. Entertainment as a Lens on Society">
          <p>
            Music and live entertainment are leading indicators of how people allocate time and money. Willingness to
            pay for a show in a given city, at a given time, under a given narrative, is a signal of that region’s
            economic and cultural state. DIZEE’s dataset, when scaled across many artists and markets, becomes a
            <strong> real-time experiment</strong> in how society reacts to world events, policy, and cultural shifts.
          </p>
          <Callout variant="primary">
            We are not only building a moat for music. We are building a window into why society reacts economically
            and sociologically to change, with entertainment data as the primary input.
          </Callout>
        </Section>

        <Section id="endgame" title="7. The Endgame: The Palantir of Music">
          <p>
            Our long-term vision is for DIZEE to become the Palantir of the music industry. Not through hype, but through
            data: who uses the platform, what we learn from their behavior, and how we turn that into predictions and
            insights that no one else can offer.
          </p>
          <p>
            The belief is that music and entertainment data, properly collected and analyzed, can explain and predict
            the way the world moves. That is the destination.
          </p>
          <CodeBlock>{`Path: Onboard artists & labels → Aggregate signals → Predict markets → Intelligence layer`}</CodeBlock>
        </Section>

        <Section id="world-changing" title="8. Why This Is World-Changing">
          <p>
            The concept becomes invaluable when it is treated as infrastructure, not just a product. No one else
            aggregates behavioral entertainment data at this depth, anonymizes it, and turns it into predictive
            intelligence. That creates durable, non-replicable advantages.
          </p>
          <ul className="list-disc list-inside space-y-2 text-[#111]/90">
            <li><strong>Policy and urban planning.</strong> Cities and regions can use entertainment demand as a leading indicator of economic health, tourism recovery, and cultural vitality. Where people spend on live events predicts where they will invest, work, and live.</li>
            <li><strong>First longitudinal dataset of its kind.</strong> Academics and researchers have never had access to large-scale, real-time data linking entertainment behavior to societal outcomes. DIZEE enables a new field: the economics and sociology of attention and culture.</li>
            <li><strong>Cross-industry intelligence.</strong> Brands, media, and governments can use the same signals for advertising, content strategy, and policy. One data backbone serves music, adjacent entertainment, and broader economic forecasting.</li>
            <li><strong>Cultural GDP in real time.</strong> Today “culture” is hard to measure. DIZEE makes it observable: willingness to pay, velocity of adoption, and regional variation. That defines a real-time index of cultural and economic momentum.</li>
            <li><strong>Crisis and recovery.</strong> How fast a region returns to spending on live events after a shock (economic, health, or political) is a proxy for resilience. DIZEE can track that at scale and over time.</li>
            <li><strong>Single source of truth.</strong> The industry has always been fragmented. Labels, promoters, and artists each see a slice. A neutral, aggregated layer reduces information asymmetry and gives every participant a shared reality to act on.</li>
          </ul>
          <Callout variant="primary">
            The goal is not to replace human judgment. It is to give the world a shared, data-driven view of how
            entertainment reflects and shapes society, so that artists, institutions, and policymakers can make better
            decisions.
          </Callout>
        </Section>

        <div className="mt-16 rounded-2xl border-2 border-[#111]/20 bg-[#111]/[0.04] p-8 text-left">
          <p className="text-xl font-bold text-[#111]">
            DIZEE does not promise that every artist will win. It promises that the industry will finally have a
            single source of truth for how music and entertainment move the world.
          </p>
        </div>
      </article>

      <footer className="border-t border-[#111]/10 bg-[#fafafa] py-6">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <p className="text-xs text-[#111]/50">© {new Date().getFullYear()} DIZEE. Confidential.</p>
        </div>
      </footer>
    </div>
  )
}
